import { IonIcon, IonSpinner } from "@ionic/react";
import { closeOutline, notificationsOutline } from "ionicons/icons";
import { ModalSheetAutoHeight } from "@/components/modal/sheet/auto-height";

interface InfoModalProps {
  /** Modal reference for controlling open/close */
  modalRef: React.RefObject<HTMLIonModalElement>;
  /** Title of the info modal */
  title: string;
  /** Content sections with optional titles and paragraphs */
  sections: Array<{
    title?: string;
    content: string | string[];
  }>;
  /** Optional push notification toggle */
  showPushToggle?: boolean;
  /** Whether push notifications are currently enabled */
  pushEnabled?: boolean;
  /** Callback when push toggle changes */
  onPushToggle?: () => void;
  /** Whether push toggle is in loading state */
  isPushLoading?: boolean;
}

/**
 * Generic info modal for displaying helpful information to users. Uses auto-height sheet modal with
 * professional styling. Optionally includes a push notification toggle.
 */
export function InfoModal({
  modalRef,
  title,
  sections,
  showPushToggle,
  pushEnabled,
  onPushToggle,
  isPushLoading,
}: InfoModalProps) {
  return (
    <ModalSheetAutoHeight ref={modalRef}>
      <div className="flex max-h-[80vh] flex-col gap-5 px-6 pb-6 pt-4">
        {/* Header with title and close */}
        <div className="flex shrink-0 flex-col gap-3">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold leading-[1.6] text-txt-primary">{title}</h2>
            <button
              type="button"
              onClick={() => modalRef.current?.dismiss()}
              className="flex size-12 items-center justify-center"
            >
              <IonIcon icon={closeOutline} className="text-2xl text-txt-primary" />
            </button>
          </div>

          {/* Description - a few lines explaining what this is about */}
          <p className="text-base font-medium leading-[1.5] text-txt-tertiary">
            {sections.length > 0 && typeof sections[0].content === "string"
              ? sections[0].content
              : Array.isArray(sections[0]?.content)
                ? sections[0].content[0]
                : ""}
          </p>
        </div>

        {/* Push notification toggle */}
        {showPushToggle && (
          <div className="flex shrink-0 items-center justify-between rounded-lg bg-surface-primary p-4">
            <div className="flex items-center gap-3">
              <div className="bg-brand-500/10 flex h-10 w-10 items-center justify-center rounded-full">
                <IonIcon icon={notificationsOutline} className="text-xl text-brand-500" />
              </div>
              <div className="flex flex-col">
                <span className="text-sm font-semibold text-txt-primary">Pushnotificaties</span>
                <span className="text-xs text-txt-secondary">
                  {pushEnabled ? "Ingeschakeld" : "Uitgeschakeld"}
                </span>
              </div>
            </div>
            <button
              type="button"
              onClick={onPushToggle}
              disabled={isPushLoading}
              className={`relative h-8 w-14 rounded-full transition-colors ${
                pushEnabled ? "bg-brand-500" : "bg-surface-tertiary"
              } ${isPushLoading ? "opacity-50" : ""}`}
            >
              {isPushLoading ? (
                <div className="flex h-full items-center justify-center">
                  <IonSpinner className="h-4 w-4 text-white" />
                </div>
              ) : (
                <span
                  className={`absolute top-1 flex h-6 w-6 items-center justify-center rounded-full bg-white shadow-md transition-transform ${
                    pushEnabled ? "translate-x-7" : "translate-x-1"
                  }`}
                />
              )}
            </button>
          </div>
        )}

        {/* Content sections - tightly grouped with gap-2 */}
        <div className="flex min-h-0 flex-1 flex-col gap-5 overflow-y-auto">
          {sections.slice(1).map((section, index) => (
            <div key={index} className="flex flex-col gap-2">
              {section.title && (
                <h3 className="text-base font-semibold leading-[1.5] tracking-[0.08px] text-txt-primary">
                  {section.title}
                </h3>
              )}
              <div className="flex flex-col gap-2">
                {Array.isArray(section.content) ? (
                  section.content.map((paragraph, pIndex) => (
                    <p
                      key={pIndex}
                      className="text-base font-medium leading-[1.5] text-txt-tertiary"
                    >
                      {paragraph}
                    </p>
                  ))
                ) : (
                  <p className="text-base font-medium leading-[1.5] text-txt-tertiary">
                    {section.content}
                  </p>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Close button */}
        <button
          type="button"
          onClick={() => modalRef.current?.dismiss()}
          className="w-full shrink-0 rounded-xl bg-button-primary py-3 text-center text-base font-semibold text-txt-inverted"
        >
          Begrepen
        </button>
      </div>
    </ModalSheetAutoHeight>
  );
}
